import React from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  checkAdvanceReference,
  confirmAdvance,
  confirmRefund,
  getRecordTimeline,
  getReservationAttachments,
  getAdvances,
  getRefunds,
  getOdooAccounts,
  updateAdvanceStatus,
  updateRefundBankDetails,
  updateRefundStatus
} from '../api/accountsApi';
import { requestJson } from '../api/http';
import { queryKeys } from '../api/queryKeys';
import { NexusModal } from '../components/NexusModal';
import { dateInputValue } from '../utils/date';

const ADVANCE_COLUMNS = [
  { key: 'Pending', label: 'Pending', tone: 'pending' },
  { key: 'Confirmation Required', label: 'Confirmation Required', tone: 'required' },
  { key: 'Not Received', label: 'Not Received', tone: 'not-received' },
  { key: 'Confirmed', label: 'Confirmed', tone: 'received' }
];
const REFUND_COLUMNS = [
  { key: 'Pending', label: 'Pending', tone: 'pending' },
  { key: 'In Processing', label: 'In Processing', tone: 'required' },
  { key: 'On Hold', label: 'On Hold', tone: 'not-received' },
  { key: 'Confirmed', label: 'Refunded', tone: 'received' }
];
const RESERVATION_STATUS_OPTIONS = ['Reserved', 'Query', 'Cancelled', 'Completed'];
const ADVANCE_ACCOUNTS = {
  '1126001': 'Meezan Bank (MBL)',
  '1126005': 'Dubai Islamic Bank (DIB)',
  '1126006': 'Bank Al Habib (BAHL)',
  '1126007': 'Bank of Punjab (BOP)',
  '1126013': 'United Bank Limited (UBL)',
  '1126008': 'Cash in Hand - Gulberg Till',
  '1126009': 'Cash in Hand - Ada Plot Till',
  '1126010': 'Cash in Hand - Head Office'
};

function advanceAccountLabel(code, accountLabels = ADVANCE_ACCOUNTS) {
  const value = String(code || '').trim();
  return value ? (accountLabels[value] || ADVANCE_ACCOUNTS[value] || value) : 'Yet to Confirm';
}

function currentUser() {
  try {
    return JSON.parse(window.localStorage.getItem('user') || '{}') || {};
  } catch {
    return {};
  }
}

function userCanViewAccounts() {
  const team = String(currentUser().Team || currentUser().team || 'Management').toLowerCase();
  return team === 'management' || team === 'accounts staff' || team === 'view-only';
}

function userCanManageAccounts() {
  const team = String(currentUser().Team || currentUser().team || 'Management').toLowerCase();
  return team === 'management' || team === 'accounts staff';
}

function fmt(value) {
  const number = Number(value || 0);
  return Number.isInteger(number) ? number.toLocaleString() : number.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function dateLabel(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Karachi' });
}

function AccountDates({ record, refund = false }) {
  return <>
    <div className="accounts-mini">Created Date: {dateLabel(record.createdDate)}</div>
    <div className="accounts-mini">Booking Date: {dateLabel(record.bookingDate || record.checkin)}</div>
    {refund ? <>
      <div className="accounts-mini">Refund Requested Date: {record.refundRequestedAt ? dateLabel(record.refundRequestedAt) : 'Not recorded (date filter uses booking date)'}</div>
      <div className="accounts-mini">Refund Source: {record.refundOrigin || record.advanceHandling || 'Not recorded'}</div>
      {record.refundRemarks ? <div className="accounts-mini">Refund Remarks: {record.refundRemarks}</div> : null}
    </> : null}
  </>;
}

function refundReceiptConfirmed(record = {}) {
  const amount = Number(record.advanceReceivedAmount || 0);
  return String(record.advanceConfirmationStatus || '').trim().toLowerCase() === 'confirmed'
    && Number.isFinite(amount) && amount > 0;
}

function absoluteAttachmentUrl(url = '') {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `https://teable.namuve.com${url}`;
  return `https://teable.namuve.com/${url}`;
}

function proxiedAttachmentUrl(url = '') {
  const absoluteUrl = absoluteAttachmentUrl(url);
  return absoluteUrl ? `/api/attachment-proxy?url=${encodeURIComponent(absoluteUrl)}` : '';
}

function firstAttachment(record = {}) {
  record = record || {};
  const stored = (record.attachments || []).find(file => /^\/api\/attachments\/[0-9a-f-]{36}$/i.test(file.url || ''));
  if (stored) return { name: stored.fileName || stored.name || 'Attachment', url: stored.url,
    mimeType: stored.mimeType || '', status: 'ready' };
  const legacy = record.raw?.fields?.Attachment?.[0];
  if (record.legacyAttachmentPending ?? Boolean(legacy)) {
    return { name: record.legacyAttachmentName || legacy?.name || 'Older attachment', url: '', status: 'legacy' };
  }
  return null;
}

function SafeAttachmentImage({ url, name, fallback = 'Preview unavailable' }) {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [url]);
  return url && !failed
    ? <img src={url} alt={name} loading="lazy" onError={() => setFailed(true)} />
    : <span className="accounts-image-fallback">{fallback}</span>;
}

function firstRefundAttachment(record = {}) {
  record = record || {};
  const list = Array.isArray(record.refundAttachment)
    ? record.refundAttachment
    : Array.isArray(record.raw?.fields?.['Refund Attachment'])
      ? record.raw.fields['Refund Attachment']
      : [];
  const attachment = list[0] || null;
  if (!attachment) return null;
  return {
    name: attachment.name || 'Refund Attachment',
    url: proxiedAttachmentUrl(attachment.lgThumbnailUrl || attachment.smThumbnailUrl || attachment.presignedUrl || ''),
    openUrl: proxiedAttachmentUrl(attachment.presignedUrl || attachment.lgThumbnailUrl || attachment.smThumbnailUrl || ''),
    downloadUrl: proxiedAttachmentUrl(attachment.presignedUrl || attachment.lgThumbnailUrl || attachment.smThumbnailUrl || '')
  };
}

function quickRange(key) {
  const today = new Date();
  const start = new Date(today);
  const end = new Date(today);
  if (key === 'yesterday') {
    start.setDate(today.getDate() - 1);
    end.setDate(today.getDate() - 1);
  } else if (key === 'last7') {
    start.setDate(today.getDate() - 6);
  } else if (key === 'last30') {
    start.setDate(today.getDate() - 29);
  } else if (key === 'currentMonth') {
    start.setDate(1);
  } else if (key === 'previousMonth') {
    start.setMonth(today.getMonth() - 1, 1);
    end.setMonth(today.getMonth(), 0);
  }
  return { start: dateInputValue(start), end: dateInputValue(end) };
}

function statusIcon(status = '') {
  const key = String(status || '').toLowerCase();
  if (key.includes('confirmed')) return '✅';
  if (key.includes('not')) return '❌';
  if (key.includes('required') || key.includes('processing') || key.includes('hold')) return '🕒';
  return '📥';
}

function refundStatusLabel(status = '') {
  return String(status || 'Pending') === 'Confirmed' ? 'Refunded' : String(status || 'Pending');
}

function refundSyncStatusFromFields(fields = {}) {
  const remarks = String(fields['Refund Remarks'] || fields.refundRemarks || '');
  if (/Odoo refund:\s*processing/i.test(remarks)) return 'processing';
  if (/Odoo refund:\s*error/i.test(remarks)) return 'error';
  if (/Odoo refund:\s*(?!processing\b|error\b)[^|]+/i.test(remarks)) return 'synced';
  return '';
}

function accountStatusLabel(status = '', type = 'advances') {
  if (type === 'refunds' && String(status || '') === 'Confirmed') return 'Refunded';
  if (type === 'advances' && String(status || '') === 'Confirmed') return 'Confirmed';
  return String(status || 'Pending');
}

function patchAccountQueryRecords(queryClient, type, recordId, patch = {}) {
  queryClient.setQueriesData({ queryKey: ['accounts', type] }, current => {
    if (!current?.records) return current;
    return {
      ...current,
      records: current.records.map(record => record.id === recordId ? { ...record, ...patch } : record)
    };
  });
}

function ReservationStatusMultiSelect({ value = [], onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const label = !selected.length
    ? 'All Reservations'
    : selected.length === 1
      ? selected[0]
      : `${selected.length} selected`;

  function toggle(option) {
    const next = selected.includes(option)
      ? selected.filter(item => item !== option)
      : [...selected, option];
    onChange(next);
  }

  return (
    <details className="accounts-multi-select">
      <summary className="accounts-multi-select-trigger">{label}</summary>
      <div className="accounts-multi-select-menu">
        {RESERVATION_STATUS_OPTIONS.map(option => (
          <label key={option} className="accounts-multi-select-option">
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => toggle(option)}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </details>
  );
}

function AccountsFilters({ filters, setFilters, exportUrl, onApply, type }) {
  function setQuick(value) {
    if (value === 'custom') return setFilters(current => ({ ...current, quick: value }));
    setFilters(current => ({ ...current, quick: value, ...quickRange(value) }));
  }
  return (
    <>
      <div className="filters-bar accounts-filters">
        <div className="filter-group">
          <span className="filter-label">Range</span>
          <select value={filters.quick} onChange={event => setQuick(event.target.value)}>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 Days</option>
            <option value="last30">Last 30 Days</option>
            <option value="currentMonth">Current Month</option>
            <option value="previousMonth">Previous Month</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>
        <div className="filter-group">
          <span className="filter-label">Date Field</span>
          <select value={filters.dateField} onChange={event => setFilters(current => ({ ...current, dateField: event.target.value }))}>
            <option value="All">All (no date restriction)</option>
            <option value="Reservation Created Date">Created Date</option>
            <option value="Booking Date">Booking Date</option>
            {type === 'refunds' ? <option value="Refund Requested At">Refund Requested Date (booking date if missing)</option> : null}
          </select>
        </div>
        <div className="filter-group"><span className="filter-label">Start</span><input type="date" disabled={filters.dateField === 'All'} value={filters.start} onChange={event => setFilters(current => ({ ...current, quick: 'custom', start: event.target.value }))} /></div>
        <div className="filter-group"><span className="filter-label">End</span><input type="date" disabled={filters.dateField === 'All'} value={filters.end} onChange={event => setFilters(current => ({ ...current, quick: 'custom', end: event.target.value }))} /></div>
        <div className="filter-group"><span className="filter-label">Status</span><select value={filters.status} onChange={event => setFilters(current => ({ ...current, status: event.target.value }))}>
          <option value="">All</option>
          {type === 'refunds' ? <>
            <option>Pending</option><option>In Processing</option><option>On Hold</option><option value="Confirmed">Refunded</option>
          </> : <>
            <option>Pending</option><option>Confirmation Required</option><option value="Confirmed">Confirmed</option><option>Not Received</option>
          </>}
        </select></div>
        <div className="filter-group">
          <span className="filter-label">Reservation Status</span>
          <ReservationStatusMultiSelect value={filters.reservationStatus} onChange={value => setFilters(current => ({ ...current, reservationStatus: value }))} />
        </div>
        <div className="filter-group"><span className="filter-label">View</span><select value={filters.view} onChange={event => setFilters(current => ({ ...current, view: event.target.value }))}><option value="kanban">Kanban</option><option value="list">List</option></select></div>
        <button type="button" className="btn-primary accounts-apply" onClick={onApply}>Apply</button>
        <a className="btn-secondary accounts-export" href={exportUrl}>Download CSV</a>
      </div>
      <div className="accounts-search-row">
        <input className="search-bar" value={filters.search} onChange={event => setFilters(current => ({ ...current, search: event.target.value }))} placeholder="Search by name, number, reservation ID, reference..." onKeyDown={event => { if (event.key === 'Enter') onApply(); }} />
        <button type="button" className="btn-secondary" onClick={onApply}>Search</button>
      </div>
    </>
  );
}

function AdvanceCard({ record, onOpen, onStatus, onHistory, canManage, accountLabels }) {
  const attachment = firstAttachment(record);
  const status = record.advanceConfirmationStatus || 'Pending';
  const syncing = ['pending', 'processing'].includes(String(record.odooAdvanceSyncStatus || '').toLowerCase());
  return (
    <div className="accounts-card">
      <div className="accounts-card-head">
        <div>
          <strong>#{record.hostawayId || record.reservationId || record.id}</strong>
          <span>{record.name}</span>
        </div>
        <div className="accounts-card-actions">
          <button type="button" className="accounts-icon-btn" title="Advance history" onClick={event => { event.stopPropagation(); onHistory(record); }}>◷</button>
          <span className="accounts-badge">{statusIcon(status)} {status}</span>
        </div>
      </div>
      <div className="accounts-mini">{record.reservationType || 'CSR-Ops'} · {record.phone}</div>
      <div className="accounts-mini">Reservation Status: {record.reservationStatus || record.status || '-'}</div>
      <div className="accounts-mini">{dateLabel(record.bookingDate || record.checkin)} · {record.slot || '-'}</div>
      <div className="accounts-money">Advance Rs {fmt(record.advAmount)}</div>
      <AccountDates record={record} />
      <div className="accounts-mini">Received into: {advanceAccountLabel(record.advanceAccountCode, accountLabels)}</div>
      <div className="accounts-mini">Confirmation status: {status === 'Confirmed' ? 'Confirmed' : 'Yet to Confirm'}</div>
      {record.advanceReferenceNumber ? <div className="accounts-mini">Payment reference: {record.advanceReferenceNumber}</div> : null}
      {record.odooAdvanceEntryReference ? <div className="accounts-mini">Odoo Advance Entry Reference: {record.odooAdvanceEntryReference}</div> : null}
      {syncing ? <div className="accounts-sync-skeleton" role="status" aria-label="Recording advance in Odoo"><span/><span/><small>Recording in Odoo in the background…</small></div> : null}
      {record.odooAdvanceSyncStatus === 'error' ? <div className="accounts-warning">Odoo recording failed. Please retry or contact support.</div> : null}
      {attachment ? <button type="button" className="accounts-thumb" onClick={() => onOpen(record)}>
        {attachment.url && attachment.mimeType.startsWith('image/')
          ? <SafeAttachmentImage url={attachment.url} name={attachment.name} fallback="File" />
          : <span className="accounts-image-fallback">File</span>}
        <span>{attachment.status === 'legacy' ? 'Review older attachment' : 'Review attachment'}</span>
      </button> : <div className="accounts-warning">{record.attachmentUnavailable
        ? 'Older screenshot is unavailable. Ask Management to re-upload it.'
        : 'No screenshot attached'}</div>}
      {!canManage ? <div className="accounts-mini">View-Only access</div> : null}
    </div>
  );
}

function RefundCard({ record, onOpen, onStatus, onHistory, canManage, accountLabels }) {
  const status = record.refundStatus || 'Pending';
  const receiptBlocked = status !== 'Confirmed' && !refundReceiptConfirmed(record);
  const bankComplete = record.bankName && record.accountNumberOrIBAN && record.accountTitle;
  const attachment = firstRefundAttachment(record);
  const syncing = record.odooRefundSyncStatus === 'processing';
  return (
    <div className="accounts-card">
      <div className="accounts-card-head">
        <div>
          <strong>#{record.hostawayId || record.reservationId || record.id}</strong>
          <span>{record.name}</span>
        </div>
        <div className="accounts-card-actions">
          <button type="button" className="accounts-icon-btn" title="Refund history" onClick={event => { event.stopPropagation(); onHistory?.(record); }}>◷</button>
          <span className="accounts-badge">{statusIcon(status)} {refundStatusLabel(status)}</span>
        </div>
      </div>
      <div className="accounts-mini">{record.phone} · {dateLabel(record.bookingDate || record.checkin)}</div>
      <div className="accounts-mini">Reservation Status: {record.reservationStatus || record.status || '-'}</div>
      <div className="accounts-money">Refund Rs {fmt(record.refundAmount || record.advanceReceivedAmount || record.advAmount)}</div>
      <AccountDates record={record} refund />
      {receiptBlocked ? <div className="accounts-warning">Confirm receipt first in Advance Processing</div> : null}
      {record.odooBookingChargeEntryReference ? <div className="accounts-badge">Non-Refundable · {record.odooBookingChargeEntryReference}</div> : null}
      {record.refundAccountCode ? <div className="accounts-mini">Refund from: {advanceAccountLabel(record.refundAccountCode, accountLabels)}</div> : null}
      {record.odooRefundEntryReference ? <div className="accounts-mini">Odoo Refund Entry Reference: {record.odooRefundEntryReference}</div> : null}
      {syncing ? <div className="accounts-sync-skeleton" role="status" aria-label="Recording refund in Odoo"><span/><span/><small>Recording refund in Odoo in the background…</small></div> : null}
      {record.odooRefundSyncStatus === 'error' ? <div className="accounts-warning">Odoo refund recording failed. Please retry or contact support.</div> : null}
      <div className={bankComplete ? 'accounts-ok' : 'accounts-warning'}>{bankComplete ? '✔ Bank Account Provided' : '⚠ Bank Account Required'}</div>
      {attachment ? <button type="button" className="accounts-thumb" onClick={() => onOpen(record)}><img src={attachment.url} alt={attachment.name} /><span>Review refund attachment</span></button> : null}
      {attachment ? <a className="btn-secondary accounts-download-link" href={attachment.downloadUrl} download={attachment.name} onClick={event => event.stopPropagation()}>Download Attachment</a> : null}
      <button type="button" className="btn-secondary" onClick={() => onOpen(record)}>{status === 'Confirmed' ? 'Review Attachment' : receiptBlocked ? 'Review Request' : 'Process Refund'}</button>
      <select value={status} disabled={!canManage} onChange={event => {
        const nextStatus = event.target.value;
        if (nextStatus === status) return;
        if (nextStatus === 'Confirmed') return onOpen(record);
        onStatus(record, nextStatus);
      }}>
        <option>Pending</option>
        <option disabled={receiptBlocked}>In Processing</option>
        <option>On Hold</option>
        <option value="Confirmed" disabled={receiptBlocked}>Refunded</option>
      </select>
      {!canManage ? <div className="accounts-mini">View-Only access</div> : null}
    </div>
  );
}

function AccountsKanban({ records, columns, type, onOpen, onStatus, onHistory, canManage, accountLabels }) {
  const Card = type === 'refunds' ? RefundCard : AdvanceCard;
  const statusField = type === 'refunds' ? 'refundStatus' : 'advanceConfirmationStatus';
  return (
    <div className="accounts-kanban">
      {columns.map(rawColumn => {
        const column = typeof rawColumn === 'string' ? { key: rawColumn, label: rawColumn, tone: '' } : rawColumn;
        const items = records.filter(record => String(record[statusField] || 'Pending') === column.key);
        return (
          <div className={`accounts-column accounts-column-${column.tone || 'default'}`} key={column.key}>
            <div className="accounts-column-head"><strong>{column.label}</strong><span>{items.length}</span></div>
            <div className="accounts-column-body">
              {items.length ? items.map(record => <Card key={record.id} record={record} onOpen={onOpen} onStatus={onStatus} onHistory={onHistory} canManage={canManage} accountLabels={accountLabels} />) : <div className="empty-state"><p>No records.</p></div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AccountsList({ records, type, onOpen, canManage }) {
  return (
    <div className="table-wrap accounts-table">
      <table>
        <thead><tr><th>Reservation</th><th>Guest</th><th>Date</th><th>Amount</th><th>Status</th><th>Reference</th><th>Action</th></tr></thead>
        <tbody>
          {records.map(record => (
            <tr key={record.id}>
              <td>#{record.hostawayId || record.reservationId || record.id}</td>
              <td>{record.name}<div className="kanban-mini">{record.phone}</div></td>
              <td><AccountDates record={record} refund={type === 'refunds'} /></td>
              <td>Rs {fmt(type === 'refunds' ? (record.refundAmount || record.advAmount) : record.advAmount)}</td>
              <td>{accountStatusLabel(type === 'refunds' ? record.refundStatus : record.advanceConfirmationStatus, type)}</td>
              <td>{record.advanceReferenceNumber || '-'}</td>
              <td><button type="button" className="btn-secondary" onClick={() => onOpen(record)}>{type === 'refunds' ? (canManage ? 'Process' : 'Review') : (canManage ? 'Review' : 'View')}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DuplicateReferenceCard({ duplicate, onHistory }) {
  if (!duplicate) return null;
  return (
    <div className="accounts-duplicate-card">
      <div>
        <span className="accounts-duplicate-kicker">Reference already used</span>
        <strong>Reservation #{duplicate.reservationId}</strong>
        <small>{duplicate.name || 'Unknown Guest'} · {duplicate.phone || duplicate.referenceNumber || ''}</small>
      </div>
      <button type="button" className="accounts-icon-btn" title="Open history" onClick={() => onHistory?.({ id: duplicate.recordId, name: duplicate.name, reservationId: duplicate.reservationId })}>◷</button>
    </div>
  );
}

function AdvanceModal({ record, onClose, mutation, onHistory, canManage }) {
  const queryClient = useQueryClient();
  const accountQuery = useQuery({ queryKey: ['odoo-accounts'], queryFn: getOdooAccounts, staleTime: 60000 });
  const advanceAccounts = accountQuery.data?.accounts || [];
  const attachmentQuery = useQuery({
    queryKey: ['accounts', 'reservation-attachments', record?.id],
    queryFn: () => getReservationAttachments(record.id),
    enabled: Boolean(record?.id), staleTime: 30000, retry: false
  });
  React.useEffect(() => {
    if (!record?.id || !attachmentQuery.data) return;
    const files = attachmentQuery.data.attachments || [];
    patchAccountQueryRecords(queryClient, 'advances', record.id, {
      attachments: files.filter(file => file.url && file.status !== 'unavailable'),
      legacyAttachmentPending: false,
      attachmentUnavailable: files.some(file => file.status === 'unavailable')
    });
  }, [queryClient, record?.id, attachmentQuery.data]);
  const [form, setForm] = React.useState(() => ({
    status: record?.advanceConfirmationStatus || 'Confirmed',
    referenceNumber: record?.advanceReferenceNumber || '',
    advanceAccountCode: record?.advanceAccountCode || '',
    amountReceived: record?.advanceReceivedAmount || record?.advAmount || 0,
    remarks: record?.advanceRemarks || ''
  }));
  const [referenceSearch, setReferenceSearch] = React.useState('');
  const odooAdvanceLocked = record?.odooAdvanceSyncStatus === 'synced';
  const receivedAccountMissing = form.status === 'Confirmed' && !String(form.advanceAccountCode || '').trim();
  React.useEffect(() => {
    setForm({
      status: record?.advanceConfirmationStatus || 'Confirmed',
      referenceNumber: record?.advanceReferenceNumber || '',
      advanceAccountCode: record?.advanceAccountCode || '',
      amountReceived: record?.advanceReceivedAmount || record?.advAmount || 0,
      remarks: record?.advanceRemarks || ''
    });
    setReferenceSearch('');
  }, [record?.id, record?.advanceConfirmationStatus, record?.advanceReferenceNumber, record?.advanceAccountCode, record?.advanceReceivedAmount, record?.advAmount, record?.advanceRemarks]);
  const referenceCheck = useQuery({
    queryKey: ['accounts', 'advance-reference-check', referenceSearch, record?.id],
    queryFn: () => checkAdvanceReference({ referenceNumber: referenceSearch, recordId: record?.id }),
    enabled: Boolean(record?.id && String(referenceSearch || '').trim().length >= 2),
    staleTime: 10000,
    retry: false
  });
  const activityQuery = useQuery({
    queryKey: ['accounts', 'advance-latest-activity', record?.id],
    queryFn: () => getRecordTimeline(record.id),
    enabled: Boolean(record?.id),
    staleTime: 15000,
    retry: false
  });
  if (!record) return null;
  const searchedCurrentReference = String(referenceSearch || '').trim()
    && String(referenceSearch || '').trim() === String(form.referenceNumber || '').trim();
  const duplicate = mutation.error?.data?.duplicate || (searchedCurrentReference ? referenceCheck.data?.duplicate : null);
  const latestActivity = (activityQuery.data?.entries || []).find(entry => entry.source === 'advance-payment-log' || String(entry.eventType || '').toLowerCase().includes('advance'));
  return (
    <NexusModal open={Boolean(record)} onClose={onClose} maxWidth={940}>
      <div className="accounts-modal-head"><div><h2>Confirm Advance</h2><p>{record.name} · #{record.hostawayId || record.id}</p></div><button className="btn-secondary" onClick={onClose}>Close</button></div>
      <div className="accounts-modal-grid accounts-modal-grid-compact">
        <div className="accounts-attachment-frame accounts-advance-attachment-frame">
          {attachmentQuery.isLoading ? <div className="empty-state"><p>Loading attachments…</p></div> : null}
          {attachmentQuery.error ? <div className="empty-state"><p>{attachmentQuery.error.message}</p><button type="button" className="btn-secondary" onClick={() => attachmentQuery.refetch()}>Retry</button></div> : null}
          {!attachmentQuery.isLoading && !attachmentQuery.error && !(attachmentQuery.data?.attachments || []).length
            ? <div className="empty-state"><p>No payment screenshot.</p></div> : null}
          {(attachmentQuery.data?.attachments || []).map(file => {
            const name = file.fileName || file.name || 'Attachment';
            const available = Boolean(file.url && file.status !== 'unavailable');
            const isImage = String(file.mimeType || '').startsWith('image/');
            return <div className="accounts-advance-attachment" key={file.id}>
              <strong>{name}</strong>
              {available && isImage ? <SafeAttachmentImage url={file.url} name={name} /> : null}
              {!available ? <div className="accounts-warning">Older file unavailable. Ask Management to re-upload it from the original copy.</div> : null}
              {available ? <div className="accounts-attachment-actions">
                <a className="btn-secondary" href={file.url} target="_blank" rel="noopener noreferrer">Open</a>
                <a className="btn-secondary" href={`${file.url}?download=1`} download={name}>Download</a>
              </div> : null}
            </div>;
          })}
        </div>
        <div className="accounts-form">
          {latestActivity ? (
            <div className="accounts-latest-activity">
              <span>Latest Activity</span>
              <strong>{latestActivity.eventDescription || latestActivity.eventType || 'Advance updated'}</strong>
              <small>By {latestActivity.performedBy || 'Unknown User'} · {dateLabel(latestActivity.timestamp)}</small>
            </div>
          ) : null}
          <label>Status<select value={form.status} disabled={!canManage} onChange={event => setForm({ ...form, status: event.target.value })}><option value="Confirmed">Confirmed</option><option>Confirmation Required</option><option>Not Received</option></select></label>
          <label>Reference Number
            <div className="accounts-reference-row">
              <input value={form.referenceNumber} disabled={!canManage || odooAdvanceLocked} onChange={event => {
                setForm({ ...form, referenceNumber: event.target.value });
                setReferenceSearch('');
              }} />
              <button type="button" className="btn-secondary" disabled={!canManage || odooAdvanceLocked || !String(form.referenceNumber || '').trim() || referenceCheck.isFetching} onClick={() => setReferenceSearch(String(form.referenceNumber || '').trim())}>{referenceCheck.isFetching ? 'Searching...' : 'Search'}</button>
            </div>
          </label>
          {referenceCheck.isFetching ? <div className="accounts-searching">Searching reference duplication in system...</div> : null}
          <DuplicateReferenceCard duplicate={duplicate} onHistory={onHistory} />
          <label>Amount Received<input type="number" value={form.amountReceived} disabled={!canManage || odooAdvanceLocked} onChange={event => setForm({ ...form, amountReceived: event.target.value })} /></label>
          <label>Received Into
            <select value={form.advanceAccountCode} disabled={!canManage || odooAdvanceLocked} onChange={event => setForm({ ...form, advanceAccountCode: event.target.value })}>
              <option value="">Select bank or cash account</option>
              {form.advanceAccountCode && !advanceAccounts.some(item => item.code === form.advanceAccountCode) ? <option value={form.advanceAccountCode} disabled>{form.advanceAccountCode} (inactive or unavailable)</option> : null}
              {advanceAccounts.map(account => <option key={account.code} value={account.code}>{account.name} ({account.code})</option>)}
            </select>
          </label>
          {accountQuery.error ? <div className="accounts-warning">Could not load Odoo accounts: {accountQuery.error.message}</div> : null}
          {receivedAccountMissing ? <div className="accounts-warning">Select the bank or cash account before saving a Received advance.</div> : null}
          {record.odooAdvanceSyncStatus === 'synced' ? <div className="accounts-ok">✔ Recorded in Odoo · Journal Entry #{record.odooAdvanceMoveId || '-'}</div> : null}
          {record.odooAdvanceSyncStatus === 'synced' && record.odooAdvanceEntryReference ? <div className="accounts-ok">Odoo Reference: {record.odooAdvanceEntryReference}</div> : null}
          {record.odooAdvanceSyncStatus === 'error' ? <div className="accounts-warning">⚠ Odoo recording failed: {record.odooAdvanceSyncMessage || 'Retry after checking the Odoo configuration.'}</div> : null}
          <label>Remarks<textarea value={form.remarks} disabled={!canManage} onChange={event => setForm({ ...form, remarks: event.target.value })} /></label>
          {mutation.error ? <div className="accounts-warning">{mutation.error.message}</div> : null}
          {canManage ? <button className="btn-primary" disabled={mutation.isPending || Boolean(duplicate) || receivedAccountMissing} onClick={() => mutation.mutate({ recordId: record.id, ...form, status: form.status === 'Confirmed' || form.status === 'Received' ? 'Confirmed' : form.status })}>{mutation.isPending ? 'Saving...' : 'Save Confirmation'}</button> : <div className="accounts-mini">View-Only access</div>}
        </div>
      </div>
    </NexusModal>
  );
}

function RefundModal({ record, onClose, bankMutation, confirmMutation, statusMutation, canManage, bankOnly = false, canEditBank = canManage }) {
  const advanceAttachments = useQuery({ queryKey: ['accounts', 'reservation-attachments', record?.id],
    queryFn: () => getReservationAttachments(record.id), enabled: Boolean(record?.id), staleTime: 30000, retry: false });
  const accountQuery = useQuery({ queryKey: ['odoo-accounts'], queryFn: getOdooAccounts, enabled: !bankOnly, staleTime: 60000 });
  const selectableAccounts = accountQuery.data?.accounts || [];
  const [bank, setBank] = React.useState(() => ({ bankName: record?.bankName || '', accountNumberOrIBAN: record?.accountNumberOrIBAN || '', accountTitle: record?.accountTitle || '', refundRemarks: record?.refundRemarks || '', holdRemarks: record?.holdRemarks || '' }));
  const [confirm, setConfirm] = React.useState(() => ({ refundAmount: record?.refundAmount || record?.advAmount || 0, refundAccountCode: record?.advanceAccountCode || '', refundAttachment: '', refundRemarks: record?.refundRemarks || '' }));
  React.useEffect(() => {
    setBank({ bankName: record?.bankName || '', accountNumberOrIBAN: record?.accountNumberOrIBAN || '', accountTitle: record?.accountTitle || '', refundRemarks: record?.refundRemarks || '', holdRemarks: record?.holdRemarks || '' });
    setConfirm({ refundAmount: record?.refundAmount || record?.advAmount || 0, refundAccountCode: record?.advanceAccountCode || '', refundAttachment: '', refundRemarks: record?.refundRemarks || '' });
  }, [record?.id, record?.bankName, record?.accountNumberOrIBAN, record?.accountTitle, record?.refundRemarks, record?.holdRemarks, record?.refundAmount, record?.advAmount]);
  if (!record) return null;
  const bankComplete = bank.bankName && bank.accountNumberOrIBAN && bank.accountTitle;
  const refundAttachment = firstRefundAttachment(record);
  const receiptBlocked = !refundReceiptConfirmed(record);
  return (
    <NexusModal open={Boolean(record)} onClose={onClose} maxWidth={1040}>
      <div className="accounts-modal-head"><div><h2>Refund Processing</h2><p>{record.name} · #{record.hostawayId || record.id}</p></div><button className="btn-secondary" onClick={onClose}>Close</button></div>
      <div className="accounts-notice">{bankOnly ? 'Refund Info allows changes to bank name, account number or IBAN, and account title only.' : 'Enter the customer refund details and select the Odoo bank or cash account from which the refund is paid.'}</div>
      <section className="accounts-advance-proof" aria-label="Received advance attachment">
        <h3>Advance Payment Attachment</h3>
        {advanceAttachments.isPending ? <p>Loading advance attachment...</p> : null}
        {advanceAttachments.error ? <p>Advance attachment could not be loaded. Please try again later.</p> : null}
        {(advanceAttachments.data?.attachments || []).filter(file => file.url && file.status !== 'unavailable').map(file => (
          <div key={file.id || file.url}>
            {String(file.mimeType || '').startsWith('image/') ? <a href={file.url} target="_blank" rel="noopener noreferrer"><div className="accounts-attachment-frame accounts-refund-attachment-frame"><SafeAttachmentImage url={file.url} name={file.fileName || file.name || 'Advance payment screenshot'} /></div></a> : null}
            <a href={file.url} target="_blank" rel="noopener noreferrer">Open full size {file.fileName || file.name || 'advance attachment'}</a>
          </div>
        ))}
        {!advanceAttachments.isPending && !advanceAttachments.error && !(advanceAttachments.data?.attachments || []).some(file => file.url && file.status !== 'unavailable') ? <p>No advance payment attachment is linked to this reservation.</p> : null}
      </section>
      {receiptBlocked ? <div className="accounts-warning">Confirm receipt first in Advance Processing. Refund payment is locked until receipt is confirmed.</div> : null}
      {confirmMutation.isPending ? <div className="accounts-sync-skeleton accounts-modal-sync-skeleton" role="status" aria-label="Queueing refund for Odoo"><span/><span/><small>Queueing the refund for background Odoo processing…</small></div> : null}
      <div className="accounts-modal-grid">
        <div className="accounts-form">
          <label>Bank Name<input value={bank.bankName} disabled={!canEditBank} onChange={event => setBank({ ...bank, bankName: event.target.value })} /></label>
          <label>Account Number / IBAN<input value={bank.accountNumberOrIBAN} disabled={!canEditBank} onChange={event => setBank({ ...bank, accountNumberOrIBAN: event.target.value })} /></label>
          <label>Account Title<input value={bank.accountTitle} disabled={!canEditBank} onChange={event => setBank({ ...bank, accountTitle: event.target.value })} /></label>
          <label>Hold Remarks<textarea value={bank.holdRemarks} disabled={!canManage || bankOnly} onChange={event => setBank({ ...bank, holdRemarks: event.target.value })} /></label>
          {canEditBank ? <button className="btn-secondary" disabled={bankMutation.isPending} onClick={() => bankMutation.mutate({ recordId: record.id,
            bankName: bank.bankName, accountNumberOrIBAN: bank.accountNumberOrIBAN, accountTitle: bank.accountTitle,
            ...(bankOnly ? { entryPoint: 'refund-info' } : { refundRemarks: bank.refundRemarks, holdRemarks: bank.holdRemarks })
          })}>{bankMutation.isPending ? 'Saving...' : 'Save Bank Details'}</button> : null}
          {bankMutation.error ? <div className="accounts-warning">{bankMutation.error.message}</div> : null}
          {canManage && !bankOnly ? <>
            <button className="btn-secondary" disabled={receiptBlocked} onClick={() => statusMutation.mutate({ recordId: record.id, status: 'In Processing' })}>Move to In Processing</button>
            <button className="btn-secondary" onClick={() => statusMutation.mutate({ recordId: record.id, status: 'On Hold', holdRemarks: bank.holdRemarks })}>Put On Hold</button>
          </> : <div className="accounts-mini">{bankOnly ? 'Refund processing is read-only through Refund Info.' : 'View-Only access'}</div>}
        </div>
        <div className="accounts-form">
          <div className={bankComplete ? 'accounts-ok' : 'accounts-warning'}>{bankComplete ? '✔ Bank Account Provided' : '⚠ Bank Account Required'}</div>
          {refundAttachment ? (
            <>
              <div className="accounts-attachment-frame accounts-refund-attachment-frame"><img src={refundAttachment.openUrl || refundAttachment.url} alt={refundAttachment.name} /></div>
              <a className="btn-secondary accounts-download-link" href={refundAttachment.downloadUrl} download={refundAttachment.name}>Download Refund Attachment</a>
            </>
          ) : null}
          <label>Refund Amount<input type="number" value={confirm.refundAmount} disabled={!canManage || bankOnly} onChange={event => setConfirm({ ...confirm, refundAmount: event.target.value })} /></label>
          <label>Refund From Account
            <select value={confirm.refundAccountCode} disabled={!canManage || bankOnly} onChange={event => setConfirm({ ...confirm, refundAccountCode: event.target.value })}>
              <option value="">Select bank or cash account</option>
              {confirm.refundAccountCode && !selectableAccounts.some(item => item.code === confirm.refundAccountCode) ? <option value={confirm.refundAccountCode} disabled>{confirm.refundAccountCode} (inactive or unavailable)</option> : null}
              {selectableAccounts.map(account => <option key={account.code} value={account.code}>{account.name} ({account.code})</option>)}
            </select>
          </label>
          {accountQuery.error ? <div className="accounts-warning">Could not load Odoo accounts: {accountQuery.error.message}</div> : null}
          <label>Refund Screenshot<input type="file" accept="image/*" disabled={!canManage || bankOnly} onChange={async event => {
            const file = event.target.files?.[0];
            if (!file) return;
            const dataBase64 = await new Promise(resolve => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.readAsDataURL(file);
            });
            setConfirm(current => ({ ...current, refundAttachment: { fileName: file.name, mimeType: file.type, dataBase64 } }));
          }} /></label>
          <label>Refund Remarks<textarea value={confirm.refundRemarks} disabled={!canManage || bankOnly} onChange={event => setConfirm({ ...confirm, refundRemarks: event.target.value })} /></label>
          {confirmMutation.error ? <div className="accounts-warning">{confirmMutation.error.message}</div> : null}
          {canManage && !bankOnly ? <button className="btn-primary" disabled={receiptBlocked || !bankComplete || !confirm.refundAccountCode || confirmMutation.isPending || (!refundAttachment && !confirm.refundAttachment)} onClick={() => confirmMutation.mutate({ recordId: record.id, ...confirm })}>{confirmMutation.isPending ? 'Saving...' : 'Mark Refunded'}</button> : null}
        </div>
      </div>
    </NexusModal>
  );
}

function AccountsHistoryModal({ record, onClose }) {
  const historyQuery = useQuery({
    queryKey: ['accounts', 'history', record?.id],
    queryFn: () => getRecordTimeline(record.id),
    enabled: Boolean(record?.id),
    staleTime: 15000
  });
  if (!record) return null;
  const entries = historyQuery.data?.entries || [];
  const rowsForEntry = entry => Object.entries(entry.changes || {}).map(([field, value]) => (
    <div className="accounts-history-change" key={field}>
      <span>{field}</span>
      <strong>{String(value?.Previous_Value ?? 'blank')} → {String(value?.New_Value ?? 'blank')}</strong>
    </div>
  ));
  return (
    <NexusModal open={Boolean(record)} onClose={onClose} maxWidth={780}>
      <div className="accounts-modal-head">
        <div>
          <h2>Advance History</h2>
          <p>{record.name || 'Reservation'} · #{record.hostawayId || record.reservationId || record.id}</p>
        </div>
        <button className="btn-secondary" onClick={onClose}>Close</button>
      </div>
      <div className="accounts-history-list">
        {historyQuery.isLoading ? <div className="empty-state"><p>Loading history...</p></div> : null}
        {entries.length ? entries.map(entry => (
          <div className="accounts-history-entry" key={entry.id}>
            <div className="accounts-history-entry-head">
              <strong>{String(entry.eventType || 'ADVANCE_UPDATED').replace(/_/g, ' ')}</strong>
              <span>{dateLabel(entry.timestamp)} {new Date(entry.timestamp || 0).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Karachi' })} PKT</span>
            </div>
            <p>{entry.eventDescription || 'Advance payment record updated.'}</p>
            <small>By {entry.performedBy || 'Unknown User'}</small>
            {rowsForEntry(entry)}
          </div>
        )) : (!historyQuery.isLoading ? <div className="empty-state"><p>No advance history recorded yet.</p></div> : null)}
      </div>
    </NexusModal>
  );
}

function AccountsPageSkeleton() {
  return (
    <div className="accounts-page-skeleton" role="status" aria-label="Loading page">
      {[0, 1, 2, 3].map(column => (
        <div className="accounts-skeleton-column" key={column}>
          <span className="accounts-skeleton-line accounts-skeleton-title" />
          {[0, 1].map(card => (
            <div className="accounts-skeleton-card" key={card}>
              <span className="accounts-skeleton-line accounts-skeleton-heading" />
              <span className="accounts-skeleton-line" />
              <span className="accounts-skeleton-line accounts-skeleton-short" />
              <span className="accounts-skeleton-block" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function AccountsPage({ type }) {
  const queryClient = useQueryClient();
  const backgroundAdvanceIds = React.useRef(new Set());
  const backgroundRefundIds = React.useRef(new Set());
  const canManage = userCanManageAccounts();
  const [filters, setFilters] = React.useState(() => ({ quick: 'today', ...quickRange('today'), dateField: type === 'refunds' ? 'Refund Requested At' : 'Reservation Created Date', status: '', reservationStatus: [], view: 'kanban', search: '', page: 1, limit: 50 }));
  const [appliedFilters, setAppliedFilters] = React.useState(filters);
  const [selected, setSelected] = React.useState(null);
  const [historyRecord, setHistoryRecord] = React.useState(null);
  const params = React.useMemo(() => ({
    start: appliedFilters.start,
    end: appliedFilters.end,
    status: appliedFilters.status,
    reservationStatus: appliedFilters.reservationStatus,
    dateField: appliedFilters.dateField,
    search: appliedFilters.search,
    page: appliedFilters.page,
    limit: appliedFilters.limit
  }), [appliedFilters.start, appliedFilters.end, appliedFilters.status, appliedFilters.reservationStatus, appliedFilters.dateField, appliedFilters.search, appliedFilters.page, appliedFilters.limit]);
  const isRefunds = type === 'refunds';
  const root = document.getElementById(isRefunds ? 'react-accounts-refunds' : 'react-accounts-advances');
  const queryKey = isRefunds ? queryKeys.refunds(params) : queryKeys.advances(params);
  const query = useQuery({ queryKey, queryFn: () => (isRefunds ? getRefunds(params) : getAdvances(params)), placeholderData: previous => previous, refetchInterval: 120000 });
  const accountQuery = useQuery({ queryKey: ['odoo-accounts'], queryFn: getOdooAccounts, staleTime: 60000 });
  const accountLabels = Object.fromEntries((accountQuery.data?.accounts || []).map(account => [account.code, account.name]));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['accounts'] });
  };
  const advanceConfirmMutation = useMutation({
    mutationFn: confirmAdvance,
    onMutate: payload => {
      patchAccountQueryRecords(queryClient, 'advances', payload.recordId, {
        advanceConfirmationStatus: payload.status,
        advanceReferenceNumber: payload.referenceNumber,
        advanceAccountCode: payload.advanceAccountCode,
        advanceReceivedAmount: Number(payload.amountReceived || 0),
        advanceRemarks: payload.remarks || ''
      });
    },
    onSuccess: (data, payload) => {
      const fields = data?.fields || data?.record?.fields || {};
      patchAccountQueryRecords(queryClient, 'advances', payload.recordId, {
        advanceConfirmationStatus: fields['Advance Confirmation Status'] || payload.status,
        advanceReferenceNumber: fields['Advance Reference Number'] || payload.referenceNumber,
        advanceAccountCode: fields['Advance Account Code'] || payload.advanceAccountCode || '',
        advanceReceivedAmount: Number(fields['Advance Received Amount'] ?? payload.amountReceived ?? 0),
        advanceRemarks: fields['Advance Remarks'] || payload.remarks || '',
        odooAdvanceSyncStatus: data?.odooAdvanceSync?.syncState || fields['Odoo Advance Sync Status'] || '',
        odooAdvanceMoveId: data?.odooAdvanceSync?.moveId || fields['Odoo Advance Move ID'] || '',
        odooAdvanceEntryReference: data?.odooAdvanceSync?.reference || fields['Odoo Advance Entry Reference'] || '',
        odooAdvanceSyncMessage: data?.odooAdvanceSync?.message || fields['Odoo Advance Sync Message'] || ''
      });
      if (data?.background) {
        backgroundAdvanceIds.current.add(payload.recordId);
        window.toast?.('Advance confirmed. Odoo recording is continuing in the background.', 'green');
      }
      setSelected(null);
      invalidate();
      queryClient.refetchQueries({ queryKey: ['accounts', 'advances'], type: 'active' });
    },
    onError: invalidate
  });
  const advanceStatusMutation = useMutation({
    mutationFn: updateAdvanceStatus,
    onMutate: payload => patchAccountQueryRecords(queryClient, 'advances', payload.recordId, { advanceConfirmationStatus: payload.status }),
    onSuccess: invalidate,
    onError: invalidate
  });
  const refundStatusMutation = useMutation({
    mutationFn: updateRefundStatus,
    onMutate: payload => patchAccountQueryRecords(queryClient, 'refunds', payload.recordId, { refundStatus: payload.status }),
    onSuccess: invalidate,
    onError: invalidate
  });
  const bankMutation = useMutation({
    mutationFn: updateRefundBankDetails,
    onMutate: payload => {
      const patch = {
        bankName: payload.bankName,
        accountNumberOrIBAN: payload.accountNumberOrIBAN,
        accountTitle: payload.accountTitle,
        refundRemarks: payload.refundRemarks || '',
        holdRemarks: payload.holdRemarks || ''
      };
      patchAccountQueryRecords(queryClient, 'refunds', payload.recordId, patch);
      setSelected(current => current?.id === payload.recordId ? { ...current, ...patch } : current);
    },
    onSuccess: invalidate,
    onError: invalidate
  });
  const refundConfirmMutation = useMutation({
    mutationFn: confirmRefund,
    onMutate: payload => {
      patchAccountQueryRecords(queryClient, 'refunds', payload.recordId, {
        refundStatus: 'In Processing',
        refundAmount: Number(payload.refundAmount || 0),
        refundAccountCode: payload.refundAccountCode || '',
        refundRemarks: payload.refundRemarks || '',
        odooRefundSyncStatus: 'processing'
      });
    },
    onSuccess: (data, payload) => {
      if (data?.background) {
        backgroundRefundIds.current.add(payload.recordId);
        window.toast?.('Refund queued. Odoo recording is continuing in the background.', 'green');
      }
      setSelected(null);
      invalidate();
      queryClient.refetchQueries({ queryKey: ['accounts', 'refunds'], type: 'active' });
    },
    onError: invalidate
  });

  React.useEffect(() => {
    if (!userCanViewAccounts()) return;
    const active = document.getElementById(isRefunds ? 'page-refunds' : 'page-advances');
    if (active) active.classList.add('react-mounted');
  }, [isRefunds]);

  React.useEffect(() => {
    if (!userCanViewAccounts() || !window.EventSource) return undefined;
    const stream = new EventSource('/api/reservations/stream');
    const onReservationUpdate = event => {
      let update = {};
      try { update = JSON.parse(event.data || '{}'); } catch { update = {}; }
      if (update.reason === 'odoo-advance-sync' && backgroundAdvanceIds.current.has(update.recordId)) {
        backgroundAdvanceIds.current.delete(update.recordId);
        const reference = update.fields?.['Odoo Advance Entry Reference'];
        window.toast?.(`Advance recorded in Odoo${reference ? ` · ${reference}` : ''}`, 'green');
      }
      if (update.reason === 'odoo-advance-sync-error') {
        backgroundAdvanceIds.current.delete(update.recordId);
        window.toast?.('Advance could not be recorded in Odoo. Please retry or contact support.', 'red');
      }
      if (update.reason === 'odoo-refund-sync' && backgroundRefundIds.current.has(update.recordId)) {
        backgroundRefundIds.current.delete(update.recordId);
        const normalized = refundSyncStatusFromFields(update.fields || {});
        const remarks = String(update.fields?.['Refund Remarks'] || '');
        const reference = normalized === 'synced' ? remarks.match(/Odoo refund:\s*([^|]+)/i)?.[1]?.trim() : '';
        window.toast?.(`Refund recorded in Odoo${reference ? ` · ${reference}` : ''}`, 'green');
      }
      if (update.reason === 'odoo-refund-sync-error') {
        backgroundRefundIds.current.delete(update.recordId);
        window.toast?.('Refund could not be recorded in Odoo. Please retry or contact support.', 'red');
      }
      if (update.recordId && update.fields) {
        const fields = update.fields;
        patchAccountQueryRecords(queryClient, 'advances', update.recordId, {
          advanceConfirmationStatus: fields['Advance Confirmation Status'] || 'Pending',
          advanceAccountCode: fields['Advance Account Code'] || '',
          advanceReceivedAmount: Number(fields['Advance Received Amount'] || 0),
          advanceReferenceNumber: fields['Advance Reference Number'] || '',
          odooAdvanceSyncStatus: fields['Odoo Advance Sync Status'] || '',
          odooAdvanceMoveId: fields['Odoo Advance Move ID'] || '',
          odooAdvanceEntryReference: fields['Odoo Advance Entry Reference'] || '',
          odooAdvanceSyncMessage: fields['Odoo Advance Sync Message'] || ''
        });
        const refundRemarks = String(fields['Refund Remarks'] || '');
        const refundSyncStatus = refundSyncStatusFromFields(fields);
        const bookingChargeReference = refundRemarks.match(/Odoo booking charge:\s*([^|]+)/i)?.[1]?.trim() || '';
        patchAccountQueryRecords(queryClient, 'refunds', update.recordId, {
          refundStatus: fields['Refund Status'] || 'Pending',
          refundAmount: Number(fields['Refund Amount'] || 0),
          refundRemarks,
          refundAccountCode: refundRemarks.match(/Refund account:\s*(\d+)/i)?.[1] || '',
          odooRefundSyncStatus: refundSyncStatus,
          odooRefundEntryReference: refundSyncStatus === 'synced' ? refundRemarks.match(/Odoo refund:\s*([^|]+)/i)?.[1]?.trim() || '' : '',
          odooBookingChargeEntryReference: bookingChargeReference
        });
      }
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    };
    ['reservation_created', 'reservation_updated', 'reservation_actual_pax_added'].forEach(eventName => {
      stream.addEventListener(eventName, onReservationUpdate);
    });
    stream.onerror = () => {};
    return () => stream.close();
  }, [queryClient]);

  if (!root) return null;
  if (!userCanViewAccounts()) return createPortal(<div className="page-header"><h1>Accounts</h1><p>You do not have access to this section.</p></div>, root);

  function normalizeAppliedFilters(source) {
    const next = { ...source };
    const statuses = Array.isArray(next.reservationStatus)
      ? next.reservationStatus.filter(Boolean)
      : [];
    next.reservationStatus = statuses;
    if (next.start && next.end && next.end < next.start) next.end = next.start;
    return next;
  }

  const records = query.data?.records || [];
  const title = isRefunds ? 'Refund Processing' : 'Advance Payments Received';
  const subtitle = isRefunds ? 'Refund requests from reservations where advance must be returned' : 'Review and confirm received advance payments';
  const exportReservationStatus = Array.isArray(appliedFilters.reservationStatus) ? appliedFilters.reservationStatus.join(',') : '';
  const exportUrl = isRefunds
    ? `/api/refunds/export?start=${appliedFilters.start}&end=${appliedFilters.end}&dateField=${encodeURIComponent(appliedFilters.dateField)}&status=${encodeURIComponent(appliedFilters.status || '')}&reservationStatus=${encodeURIComponent(exportReservationStatus)}&search=${encodeURIComponent(appliedFilters.search || '')}`
    : `/api/advances/export?start=${appliedFilters.start}&end=${appliedFilters.end}&dateField=${encodeURIComponent(appliedFilters.dateField)}&status=${encodeURIComponent(appliedFilters.status || '')}&reservationStatus=${encodeURIComponent(exportReservationStatus)}&search=${encodeURIComponent(appliedFilters.search || '')}`;
  return createPortal(
    <>
      <div className="page-header accounts-page-head"><div><h1>{title}</h1><p>{subtitle}</p></div></div>
      <AccountsFilters type={type} filters={filters} setFilters={setFilters} exportUrl={exportUrl} onApply={() => {
        const next = normalizeAppliedFilters({ ...filters, page: 1 });
        setFilters(next);
        setAppliedFilters(next);
      }} />
      {query.isLoading ? <AccountsPageSkeleton /> : null}
      {query.error ? <div className="empty-state"><p>{query.error.message}</p></div> : null}
      {!query.isLoading && (filters.view === 'list'
        ? <AccountsList records={records} type={type} onOpen={setSelected} canManage={canManage} />
        : <AccountsKanban records={records} columns={isRefunds ? REFUND_COLUMNS : ADVANCE_COLUMNS} type={type} onOpen={setSelected} onHistory={setHistoryRecord} onStatus={(record, status) => (isRefunds ? refundStatusMutation : advanceStatusMutation).mutate({ recordId: record.id, status })} canManage={canManage} accountLabels={accountLabels} />)}
      <div className="pagination">
        <span className="page-info">Page {Number(query.data?.page || 1)} of {Number(query.data?.totalPages || 1)} · {Number(query.data?.total || 0).toLocaleString()} records</span>
        <button className="page-btn" disabled={Number(query.data?.page || 1) <= 1 || query.isFetching} onClick={() => {
          const next = { ...appliedFilters, page: Math.max(1, Number(appliedFilters.page || 1) - 1) };
          setFilters(next); setAppliedFilters(next);
        }}>Previous</button>
        <button className="page-btn" disabled={Number(query.data?.page || 1) >= Number(query.data?.totalPages || 1) || query.isFetching} onClick={() => {
          const next = { ...appliedFilters, page: Number(appliedFilters.page || 1) + 1 };
          setFilters(next); setAppliedFilters(next);
        }}>Next</button>
      </div>
      {isRefunds
        ? <RefundModal record={selected} onClose={() => setSelected(null)} bankMutation={bankMutation} confirmMutation={refundConfirmMutation} statusMutation={refundStatusMutation} canManage={canManage} />
        : <AdvanceModal record={selected} onClose={() => setSelected(null)} mutation={advanceConfirmMutation} onHistory={setHistoryRecord} canManage={canManage} />}
      <AccountsHistoryModal record={historyRecord} onClose={() => setHistoryRecord(null)} />
    </>,
    root
  );
}

export function AccountsPageMount({ type }) {
  return <AccountsPage type={type} />;
}


export function RefundInfoMount() {
  const [recordId, setRecordId] = React.useState('');
  const client = useQueryClient();
  React.useEffect(() => {
    const open = event => setRecordId(event.detail?.recordId || '');
    window.addEventListener('nexus:refund-info', open);
    return () => window.removeEventListener('nexus:refund-info', open);
  }, []);
  const info = useQuery({ queryKey: ['refund-info', recordId], queryFn: () => requestJson(`/api/refund/info/${encodeURIComponent(recordId)}`), enabled: Boolean(recordId), staleTime: 0, retry: false });
  const bankMutation = useMutation({ mutationFn: updateRefundBankDetails, onSuccess: () => {
    client.invalidateQueries({ queryKey: ['refund-info', recordId] });
    client.invalidateQueries({ queryKey: ['accounts'] });
    window.toast?.('Refund bank details saved');
  } });
  React.useEffect(() => { if (info.error) { window.toast?.(info.error.message, 'red'); setRecordId(''); } }, [info.error]);
  if (!recordId || !info.data?.record) return null;
  return <RefundModal key={recordId} record={info.data.record} onClose={() => setRecordId('')} bankMutation={bankMutation}
    confirmMutation={{ isPending: false }} statusMutation={{}} canManage={false} canEditBank={true} bankOnly />;
}
